import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import { 
  AddCircle, 
  MoreVert, 
  Devices as DevicesIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
  GetApp as GetAppIcon,
  Upload as UploadIcon
} from "@mui/icons-material";
import { 
  Button, 
  CircularProgress, 
  Grid, 
  Menu, 
  MenuItem, 
  Stack,
  TextField,
  Typography,
  IconButton,
  Paper,
  InputAdornment,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  Tooltip
} from "@mui/material";

import api from "../../services/api";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import { AuthContext } from "../../context/Auth/AuthContext";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    flex: 1,
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    height: "calc(100vh - 64px)", // Subtract header height
    overflowY: "auto",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      background: theme.palette.background.default
    },
    "&::-webkit-scrollbar-thumb": {
      background: theme.palette.divider,
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      background: theme.palette.action.hover
    }
  },
  searchContainer: {
    width: "100%",
    maxWidth: "400px"
  },
  searchInput: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: theme.palette.divider
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main
      }
    }
  },
  flowCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    transition: "all 0.3s ease",
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: theme.shadows[4]
    }
  },
  flowCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(1)
  },
  flowCardTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    width: "calc(100% - 40px)", // Account for the menu button
    "& h6": {
      fontSize: "1rem",
      fontWeight: 500,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  },
  flowCardStatus: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary
  },
  addButton: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    borderRadius: theme.shape.borderRadius,
    textTransform: "none",
    gap: theme.spacing(1),
    boxShadow: "none",
    padding: theme.spacing(1, 2),
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
      boxShadow: "none"
    }
  },
  menuItem: {
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderRadius: theme.shape.borderRadius,
    margin: theme.spacing(0.5),
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    }
  },
  paper: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    transition: "background-color 0.3s ease"
  },
  confirmDialog: {
    "& .MuiDialog-paper": {
      borderRadius: theme.shape.borderRadius * 2,
      padding: theme.spacing(2),
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary
    }
  },
  confirmDialogTitle: {
    color: theme.palette.text.primary,
    fontSize: "1.2rem",
    fontWeight: 600,
    padding: theme.spacing(2)
  },
  confirmDialogContent: {
    color: theme.palette.text.secondary,
    fontSize: "1rem",
    padding: theme.spacing(2)
  },
  confirmDialogActions: {
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    "& .MuiButton-root": {
      textTransform: "none",
      fontWeight: 500
    }
  },
  headerContainer: {
    position: "sticky",
    top: 0,
    zIndex: 1,
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2, 0),
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}));

const FlowBuilder = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [webhooks, setWebhooks] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [selectedWebhookName, setSelectedWebhookName] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDuplicateOpen, setConfirmDuplicateOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [reloadData, setReloadData] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleDeleteWebhook = async (webhookId) => {
    try {
      await api.delete(`/flowbuilder/${webhookId}`);
      setReloadData(prev => !prev);
      toast.success("Fluxo excluído com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDuplicateFlow = async (flowId) => {
    try {
      await api.post(`/flowbuilder/duplicate`, { flowId });
      setReloadData(prev => !prev);
      toast.success("Fluxo duplicado com sucesso");
    } catch (err) {
      toastError(err);
    }
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPageNumber(prev => prev + 1);
    }
  };

  const handleDoubleClick = (flowId) => {
    history.push(`/flowbuilder/${flowId}`);
  };

  const handleExportFlow = async (flowId) => {
    try {
      const { data } = await api.get(`/flowbuilder/flow/${flowId}`);
      if (data.flow) {
        const flowData = {
          nodes: data.flow.flow.nodes,
          edges: data.flow.flow.connections
        };
        const blob = new Blob([JSON.stringify(flowData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `flow-${flowId}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success('Fluxo exportado com sucesso!');
      }
    } catch (err) {
      toast.error('Erro ao exportar fluxo');
    }
  };

  const handleImportFlow = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const flowData = JSON.parse(e.target.result);
          if (flowData.nodes && flowData.edges) {
            // First create a new flow
            const { data: newFlow } = await api.post("/flowbuilder", {
              name: `Fluxo Importado ${new Date().toLocaleString()}`,
            });

            // Then update the flow with the imported data
            await api.post("/flowbuilder/flow", {
              idFlow: newFlow.id,
              nodes: flowData.nodes,
              connections: flowData.edges
            });

            toast.success('Fluxo importado com sucesso!');
            
            // Navigate to the new flow
            history.push(`/flowbuilder/${newFlow.id}`);
          } else {
            toast.error('Arquivo inválido');
          }
        } catch (err) {
          toast.error('Erro ao importar fluxo');
        }
      };
      reader.readAsText(file);
    }
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/flowbuilder");
          setWebhooks(data.flows);
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, reloadData]);

  if (user.profile !== "admin") {
    return <ForbiddenPage />;
  }

  const handleClick = (event, webhook) => {
    setAnchorEl(event.currentTarget);
    setSelectedWebhookName(webhook);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <MainContainer>
      <Box className={classes.headerContainer}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Title>Fluxos de Conversa</Title>
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              placeholder="Buscar fluxo..."
              value={searchParam}
              onChange={(e) => setSearchParam(e.target.value)}
              className={classes.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              size="small"
            />
            <input
              type="file"
              id="flow-import"
              accept=".json"
              style={{ display: 'none' }}
              onChange={handleImportFlow}
            />
            <Button
              variant="contained"
              onClick={() => document.getElementById('flow-import').click()}
              className={classes.addButton}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              Importar Fluxo
            </Button>
            <Button
              variant="contained"
              className={classes.addButton}
              onClick={() => setContactModalOpen(true)}
              startIcon={<AddCircle />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: theme.palette.primary.dark,
                }
              }}
            >
              Adicionar Fluxo
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Paper 
        className={`${classes.mainContainer} ${classes.paper}`}
        elevation={0}
        onScroll={handleScroll}
        sx={{
          backgroundColor: theme.palette.background.default,
          color: theme.palette.text.primary
        }}
      >
        <Grid container spacing={2}>
          {webhooks.map((flow) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={flow.id}>
              <Box
                className={classes.flowCard}
                onClick={() => handleDoubleClick(flow.id)}
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  borderColor: theme.palette.divider
                }}
              >
                <div className={classes.flowCardHeader}>
                  <div className={classes.flowCardTitle}>
                    <DevicesIcon color="primary" />
                    <Tooltip title={flow.name} placement="top" arrow>
                      <Typography variant="h6" noWrap>
                        {flow.name}
                      </Typography>
                    </Tooltip>
                  </div>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClick(e, flow);
                    }}
                  >
                    <MoreVert fontSize="small" />
                  </IconButton>
                </div>
                <Typography
                  variant="body2"
                  color={flow.active ? "success.main" : "text.secondary"}
                  className={classes.flowCardStatus}
                >
                  {flow.active ? "Ativo" : "Desativado"}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        {loading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        )}
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: { 
            borderRadius: 2,
            minWidth: 180,
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary
          }
        }}
      >
        <MenuItem 
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            history.push(`/flowbuilder/${selectedWebhookName.id}`);
          }}
        >
          <DevicesIcon fontSize="small" />
          Editar fluxo
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            setSelectedContactId(selectedWebhookName.id);
            setSelectedWebhookName(selectedWebhookName);
            setContactModalOpen(true);
          }}
        >
          <EditIcon fontSize="small" />
          Editar nome
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            setConfirmDuplicateOpen(true);
          }}
        >
          <ContentCopyIcon fontSize="small" />
          Duplicar
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            handleExportFlow(selectedWebhookName.id);
          }}
        >
          <GetAppIcon fontSize="small" />
          Exportar
        </MenuItem>
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            handleClose();
            setConfirmOpen(true);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
          Excluir
        </MenuItem>
      </Menu>

      <FlowBuilderModal
        open={contactModalOpen}
        onClose={() => {
          setContactModalOpen(false);
          setSelectedContactId(null);
          setSelectedWebhookName(null);
        }}
        flowId={selectedContactId}
        nameWebhook={selectedWebhookName?.name || ""}
        onSave={() => setReloadData(prev => !prev)}
      />

      <Dialog 
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        className={classes.confirmDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className={classes.confirmDialogTitle}>
          Excluir Fluxo
        </DialogTitle>
        <DialogContent className={classes.confirmDialogContent}>
          <Typography>
            Tem certeza que deseja excluir o fluxo <strong>{selectedWebhookName?.name}</strong>?
            Esta ação não poderá ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.confirmDialogActions}>
          <Button
            onClick={() => setConfirmOpen(false)}
            variant="outlined"
            sx={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleDeleteWebhook(selectedWebhookName.id);
              setConfirmOpen(false);
            }}
            variant="contained"
            color="error"
            autoFocus
            sx={{
              "&:hover": {
                backgroundColor: theme.palette.error.dark
              }
            }}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={confirmDuplicateOpen}
        onClose={() => setConfirmDuplicateOpen(false)}
        className={classes.confirmDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle className={classes.confirmDialogTitle}>
          Duplicar Fluxo
        </DialogTitle>
        <DialogContent className={classes.confirmDialogContent}>
          <Typography>
            Tem certeza que deseja duplicar o fluxo <strong>{selectedWebhookName?.name}</strong>?
            Uma nova cópia será criada.
          </Typography>
        </DialogContent>
        <DialogActions className={classes.confirmDialogActions}>
          <Button
            onClick={() => setConfirmDuplicateOpen(false)}
            variant="outlined"
            sx={{
              borderColor: theme.palette.divider,
              color: theme.palette.text.primary,
              "&:hover": {
                borderColor: theme.palette.text.primary,
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={() => {
              handleDuplicateFlow(selectedWebhookName.id);
              setConfirmDuplicateOpen(false);
            }}
            variant="contained"
            color="primary"
            autoFocus
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark
              }
            }}
          >
            Duplicar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default FlowBuilder;